import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Row,
  Skeleton,
  Typography,
  notification,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import AnalysisSection from "./AnalysisSection";
import CurrentTurnCard from "./components/CurrentTurnCard";
import DayKpis from "./components/DayKpis";
import LineCutCard from "./components/LineCutCard";
import OutcomePanel from "./components/OutcomePanel";
import PendingBar from "./components/PendingBar";
import QueueStrip from "./components/QueueStrip";
import TeamPanel from "./components/TeamPanel";

const { Text, Title } = Typography;

const POLL_INTERVAL_MS = 10000;
// Faturamento vem da sincronização do ERP, que não é tempo real: não precisa
// acompanhar o ritmo do polling da fila.
const REVENUE_POLL_INTERVAL_MS = 60000;
const UNDO_TOAST_SECONDS = 20;

export default function ListaVez() {
  const { user } = useAuth();
  const {
    getTurnList,
    getTurnListRevenue,
    postTurnListRegister,
    postTurnListPresence,
    postTurnListLineCut,
    postTurnListUndo,
    patchTurnListPending,
    getNonSaleReasons,
  } = useApi();

  // A filial é a mesma de todo o app — trocada pelo seletor global no topo
  // (BranchSelector, dentro de MenuBar), que recarrega a página ao mudar.
  // Esta tela não introduz um seletor próprio: seria um segundo controle
  // fazendo a mesma coisa que o de cima.
  const branchId = user?.active_branch_id || null;

  const [state, setState] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => new Date());
  // Verdadeiro enquanto o último refresh (poll silencioso ou carregamento)
  // teve sucesso. Alimenta o badge "ao vivo" (ver renderização mais abaixo).
  const [liveOk, setLiveOk] = useState(true);
  // erp_code -> R$ vendido hoje. null enquanto não carregou (ou se falhou),
  // para o painel não mostrar "R$ 0,00" como se fosse dado.
  const [revenueByErp, setRevenueByErp] = useState(null);

  // As funções do useApi() têm identidade nova a cada render (o hook não
  // memoiza nada). Guardá-las numa ref, atualizada a cada render, permite que
  // refresh/submit/etc dependam só dos valores de estado que realmente
  // importam (ex.: branchId) sem recriar callbacks — e sem disparar o loop
  // efeito -> setState -> render -> novo callback -> efeito de novo.
  const api = useRef(null);
  api.current = {
    getTurnList,
    getTurnListRevenue,
    postTurnListRegister,
    postTurnListPresence,
    postTurnListLineCut,
    postTurnListUndo,
    patchTurnListPending,
    getNonSaleReasons,
  };

  // Contador monotônico: cada chamada (poll ou mutação) captura o valor no
  // início e só aplica sua resposta se ninguém avançou o contador enquanto
  // ela estava em voo. Isso descarta respostas atrasadas de um poll que
  // chegam depois de uma mutação mais recente já ter atualizado a tela.
  const requestSeq = useRef(0);

  // Alvo do painel de resultado: null (fechado), "current", um item pendente,
  // ou um alvo de furo de fila ({ entry_id, seller_name, viaLineCut: true }).
  // O polling nunca mexe neste estado, então o formulário aberto sobrevive a um
  // refresh: só uma ação do usuário fecha o painel.
  const [outcomeTarget, setOutcomeTarget] = useState(null);

  // Enquanto preenchido, o card "NA VEZ" é substituído pelo card de furo de
  // fila para este vendedor — a vez real não muda, só a tela.
  const [lineCutTarget, setLineCutTarget] = useState(null);

  const selectedBranch = useMemo(
    () =>
      (user?.available_branches || []).find(
        (branch) => branch.id === branchId
      ) || null,
    [user?.available_branches, branchId]
  );

  useEffect(() => {
    (async () => {
      try {
        const data = await api.current.getNonSaleReasons(
          selectedBranch?.client_id
        );
        setReasons(Array.isArray(data) ? data.filter((r) => r.is_active) : []);
      } catch {
        setReasons([]);
      }
    })();
  }, [selectedBranch?.client_id]);

  // Limpa o estado da filial anterior assim que a filial muda, para que o
  // card/lista/pendências não fiquem na tela (e clicáveis) enquanto os
  // dados da nova filial ainda não chegaram.
  useEffect(() => {
    setState(null);
    setRevenueByErp(null);
    setOutcomeTarget(null);
    setLineCutTarget(null);
  }, [branchId]);

  useEffect(() => {
    if (!branchId) return undefined;
    let cancelled = false;
    const loadRevenue = async () => {
      try {
        const data = await api.current.getTurnListRevenue(branchId);
        if (!cancelled) setRevenueByErp(data?.revenue_by_erp_code || {});
      } catch {
        // Falha no faturamento não pode atrapalhar a operação da fila:
        // mantém o último valor bom e tenta de novo no próximo ciclo.
      }
    };
    loadRevenue();
    const timer = setInterval(loadRevenue, REVENUE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [branchId]);

  const refresh = useCallback(
    async ({ silent } = {}) => {
      if (!branchId) return;
      if (!silent) setLoading(true);
      const mySeq = ++requestSeq.current;
      try {
        const data = await api.current.getTurnList(branchId);
        if (mySeq === requestSeq.current) {
          setState(data);
          setLiveOk(true);
        }
      } catch (error) {
        if (mySeq === requestSeq.current) setLiveOk(false);
        if (!silent) {
          notification.error({
            message: "Erro ao carregar a Lista da Vez",
            description: error?.response?.data?.detail || error?.message,
          });
        }
      } finally {
        if (!silent && mySeq === requestSeq.current) setLoading(false);
      }
    },
    [branchId]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Polling. Não interrompe o painel de resultado aberto: o estado é
  // atualizado, mas o formulário só fecha por ação do usuário.
  useEffect(() => {
    const timer = setInterval(
      () => refresh({ silent: true }),
      POLL_INTERVAL_MS
    );
    const onFocus = () => refresh({ silent: true });
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  // Relógio próprio para os contadores de tempo decorrido.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const showUndo = useCallback(
    (attendanceId) => {
      if (!attendanceId) return;
      const key = `undo-${attendanceId}`;
      notification.success({
        key,
        message: "Registrado",
        duration: UNDO_TOAST_SECONDS,
        btn: (
          <Button
            size="small"
            onClick={async () => {
              notification.destroy(key);
              const mySeq = ++requestSeq.current;
              try {
                const data = await api.current.postTurnListUndo(
                  branchId,
                  attendanceId
                );
                if (mySeq === requestSeq.current) setState(data);
              } catch (error) {
                if (
                  mySeq === requestSeq.current &&
                  error?.response?.data?.state
                ) {
                  setState(error.response.data.state);
                }
                notification.error({
                  message: "Não foi possível desfazer",
                  description: error?.response?.data?.detail || error?.message,
                });
              }
            }}>
            Desfazer
          </Button>
        ),
      });
    },
    [branchId]
  );

  const submit = useCallback(
    async (payload) => {
      setBusy(true);
      const mySeq = ++requestSeq.current;
      try {
        const data = await api.current.postTurnListRegister({
          branch_id: branchId,
          ...payload,
        });
        if (mySeq === requestSeq.current) {
          setState(data);
          setOutcomeTarget(null);
          showUndo(data.last_undoable_id);
        }
      } catch (error) {
        const conflict = error?.response?.status === 409;
        if (
          conflict &&
          error?.response?.data?.state &&
          mySeq === requestSeq.current
        ) {
          setState(error.response.data.state);
          setOutcomeTarget(null);
        }
        notification.error({
          message: conflict ? "A vez mudou" : "Erro ao registrar",
          description: error?.response?.data?.detail || error?.message,
        });
      } finally {
        setBusy(false);
      }
    },
    [branchId, showUndo]
  );

  const handlePresence = useCallback(
    async (entryId, status, pauseReason) => {
      setBusy(true);
      const mySeq = ++requestSeq.current;
      try {
        const data = await api.current.postTurnListPresence({
          branch_id: branchId,
          entry_id: entryId,
          status,
          pause_reason: pauseReason || null,
        });
        if (mySeq === requestSeq.current) setState(data);
      } catch (error) {
        notification.error({
          message: "Erro ao atualizar presença",
          description: error?.response?.data?.detail || error?.message,
        });
      } finally {
        setBusy(false);
      }
    },
    [branchId]
  );

  const handleStartLineCut = useCallback((row) => {
    setLineCutTarget({ entry_id: row.entry_id, seller_name: row.seller_name });
  }, []);

  const handleCancelLineCut = useCallback(() => {
    setLineCutTarget(null);
    setOutcomeTarget(null);
  }, []);

  const submitLineCut = useCallback(
    async (payload) => {
      setBusy(true);
      const mySeq = ++requestSeq.current;
      try {
        const data = await api.current.postTurnListLineCut({
          branch_id: branchId,
          ...payload,
        });
        if (mySeq === requestSeq.current) {
          setState(data);
          setLineCutTarget(null);
          setOutcomeTarget(null);
          showUndo(data.last_undoable_id);
        }
      } catch (error) {
        const conflict = error?.response?.status === 409;
        if (conflict && mySeq === requestSeq.current) {
          // A vez real chegou nesse vendedor enquanto a tela estava aberta —
          // a interação de furo de fila não faz mais sentido, ele já é a vez.
          setLineCutTarget(null);
          setOutcomeTarget(null);
          if (error?.response?.data?.state) {
            setState(error.response.data.state);
          }
        }
        notification.error({
          message: conflict ? "Este vendedor já é a vez" : "Erro ao furar fila",
          description: error?.response?.data?.detail || error?.message,
        });
      } finally {
        setBusy(false);
      }
    },
    [branchId, showUndo]
  );

  const resolvePending = useCallback(async (pendingId, payload) => {
    setBusy(true);
    const mySeq = ++requestSeq.current;
    try {
      const data = await api.current.patchTurnListPending(pendingId, payload);
      if (mySeq === requestSeq.current) {
        setState(data);
        setOutcomeTarget(null);
      }
    } catch (error) {
      notification.error({
        message: "Erro ao completar o registro",
        description: error?.response?.data?.detail || error?.message,
      });
    } finally {
      setBusy(false);
    }
  }, []);

  const handleOutcomeConfirm = useCallback(
    async (payload) => {
      if (outcomeTarget === "current") {
        await submit({
          entry_id: state?.current?.entry_id,
          outcome: "nao_venda",
          ...payload,
        });
        return;
      }
      if (outcomeTarget?.viaLineCut) {
        await submitLineCut({
          entry_id: outcomeTarget.entry_id,
          outcome: "nao_venda",
          ...payload,
        });
        return;
      }
      await resolvePending(outcomeTarget.id, {
        outcome: "nao_venda",
        ...payload,
      });
    },
    [outcomeTarget, resolvePending, state, submit, submitLineCut]
  );

  // Uma pendência (ou um furo de fila) pode ter terminado em venda — daí o
  // botão "Vendeu" no painel quando o alvo não é a vez atual.
  const handleOutcomeSale = useCallback(async () => {
    if (outcomeTarget === "current" || !outcomeTarget) return;
    if (outcomeTarget.viaLineCut) {
      await submitLineCut({
        entry_id: outcomeTarget.entry_id,
        outcome: "venda",
      });
      return;
    }
    await resolvePending(outcomeTarget.id, { outcome: "venda" });
  }, [outcomeTarget, resolvePending, submitLineCut]);

  const outcomeSellerName =
    outcomeTarget === "current"
      ? state?.current?.seller_name
      : outcomeTarget?.seller_name;

  return (
    <ConfigProvider
      theme={{
        components: { Card: { headerBg: "var(--ouran-card-header-bg)" } },
      }}>
      <div
        style={{
          width: "100%",
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
        }}>
        <div
          style={{
            width: "90%",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}>
            <Title level={3} style={{ margin: 0 }}>
              Lista da Vez
            </Title>
            <Text type="secondary">{dayjs().format("ddd, DD/MM")}</Text>
            {state?.drawn_at && (
              <Text type="secondary">
                sorteio {dayjs(state.drawn_at).format("HH:mm")}
              </Text>
            )}
            {liveOk ? (
              <Text type="success">● ao vivo</Text>
            ) : (
              <Text type="warning">● sem conexão</Text>
            )}
          </div>

          {loading && !state ? (
            <Card>
              <Skeleton active />
            </Card>
          ) : (
            <>
              <Row gutter={[20, 20]} align="top">
                <Col xs={24} lg={15}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 20,
                    }}>
                    {lineCutTarget ? (
                      <LineCutCard
                        target={lineCutTarget}
                        busy={busy}
                        onSale={() =>
                          submitLineCut({
                            entry_id: lineCutTarget.entry_id,
                            outcome: "venda",
                          })
                        }
                        onNoSale={() =>
                          setOutcomeTarget({
                            ...lineCutTarget,
                            viaLineCut: true,
                          })
                        }
                        onRegisterLater={() =>
                          submitLineCut({
                            entry_id: lineCutTarget.entry_id,
                            outcome: "pendente",
                          })
                        }
                        onCancel={handleCancelLineCut}
                      />
                    ) : (
                      <CurrentTurnCard
                        current={state?.current}
                        elapsedNow={now}
                        busy={busy}
                        onSale={() =>
                          submit({
                            entry_id: state?.current?.entry_id,
                            outcome: "venda",
                          })
                        }
                        onNoSale={() => setOutcomeTarget("current")}
                        onRegisterLater={() =>
                          submit({
                            entry_id: state?.current?.entry_id,
                            outcome: "pendente",
                          })
                        }
                        onSkip={() =>
                          submit({
                            entry_id: state?.current?.entry_id,
                            outcome: "pulado",
                          })
                        }
                      />
                    )}
                    <OutcomePanel
                      key={
                        outcomeTarget === "current"
                          ? "current"
                          : outcomeTarget?.viaLineCut
                            ? `line-cut-${outcomeTarget.entry_id}`
                            : (outcomeTarget?.id ?? "none")
                      }
                      open={outcomeTarget !== null}
                      sellerName={outcomeSellerName}
                      reasons={reasons}
                      saving={busy}
                      allowSale={
                        outcomeTarget !== null && outcomeTarget !== "current"
                      }
                      onSale={handleOutcomeSale}
                      onConfirm={handleOutcomeConfirm}
                      onCancel={() => setOutcomeTarget(null)}
                    />
                    <QueueStrip upcoming={state?.upcoming || []} />
                    <PendingBar
                      pending={state?.pending || []}
                      onResolve={(item) => setOutcomeTarget(item)}
                    />
                    {state?.kpis && <DayKpis kpis={state.kpis} />}
                  </div>
                </Col>
                <Col xs={24} lg={9}>
                  <TeamPanel
                    team={state?.team || []}
                    revenueByErp={revenueByErp}
                    elapsedNow={now}
                    busy={busy}
                    onPresence={handlePresence}
                    onStartLineCut={handleStartLineCut}
                    lineCutActive={Boolean(lineCutTarget)}
                  />
                </Col>
              </Row>
            </>
          )}

          <AnalysisSection />
        </div>
      </div>
    </ConfigProvider>
  );
}
